import cbor from 'cbor';

import state from '@/state';

import { Bridge } from './Bridge';

class PlatformResetError extends Error {}


export class BasicPlatformWasiBridge extends Bridge {
  static async constructFromUrl(wasmUrl) {
    const response = await fetch(wasmUrl);
    const wasmArrayBuffer = await response.arrayBuffer();

    const wasmModule = await WebAssembly.compile(wasmArrayBuffer);

    const bridge = new BasicPlatformWasiBridge(wasmModule);
    await bridge.instantiate();

    return bridge;
  }

  constructor(wasmModule) {
    super();

    this.wasmModule = wasmModule;
    this.resetInternalState();

    this.storageToImport = null;
  }

  resetInternalState() {

    this.wasmMemory = null;
    this.wasmMemoryBuffer = null;
    this.wasmInstance = null;
    this.wasmPrintString = '';
    this.debugProbes = [];
    this.isInitialized = true;
    this.lastPrintedLines = [];
  }

  async instantiate() {
    this.wasmMemory = new WebAssembly.Memory({ initial: 512, maximum: 512 });
    this.wasmMemory8 = new Uint8Array(this.wasmMemory.buffer);
    this.wasmMemory32 = new Uint32Array(this.wasmMemory.buffer);

    const wasmImports = {
      env: {
        memory: this.wasmMemory,
        WasiPlatform_PrintWriter: (messagePointer, length) => {
          const decoder = new TextDecoder();
          this.wasmPrintString += decoder.decode(this.wasmMemory8.slice(messagePointer, messagePointer + length));

          const index = this.wasmPrintString.lastIndexOf('\n');
          if (index >= 0) {
            const lines = this.wasmPrintString.slice(0, index);
            for (const line of lines.split('\n')) {
              if (this.lastPrintedLines.length > 20) {
                this.lastPrintedLines = this.lastPrintedLines.slice(-20);
              }
              this.lastPrintedLines.push(line);
              this.emit('log', line);
            }

            global.console.log(lines);
            this.wasmPrintString = this.wasmPrintString.slice(index + 1);
          }
        },
        WasiPlatform_LogWriter: (/*bufferPointer, length*/) => {
          // FIXME(daniel): not yet implemented
        },
        WasiPlatform_DidInitializePlatform: () => {
          const { exports } = this.wasmInstance;

          if (this.storageToImport) {
            const length = this.storageToImport.length;
            if (length <= this.sharedBufferSize) {
              for (let i = 0; i < length; i++) {
                this.wasmMemory8 [this.sharedBufferAddr + i] = this.storageToImport [i];
              }

              let error = exports.WasiPlatform_ImportStorage(this.sharedBufferAddr, length);

              if (error < 0) {
                exports.WasiPlatform_PrintAndFreeError(error);
              }
            } else {
              throw new Error(`Not enough shared memory to import storage`);
            }
          }
          if ( this.WasiPlatform_DidInitialize ) {
            this.WasiPlatform_DidInitialize();
          }
          return 0;
        },
        WasiPlatform_DidAbort: () => {
          // console.log('WasiPlatform_DidAbort');
          this.stop();
          let index = this.lastPrintedLines.findLastIndex(line => {
            return line.includes('GURU MEDITATION');
          });
          if (index < 0) {
            index = 0;
          }
          const message = this.lastPrintedLines.slice(index).join('\n');

          // this.emit('reportAbort', message);
          state.abortMessage = message;
          state.abortMessageIsVisible = true;
          // throw new Error('NYI');
        },
        WasiPlatform_DidPerformReset: (/*reason*/) => {
          // console.log(`WasiPlatform_DidPerformReset(${reason}) called`);
          throw new PlatformResetError();
        },
        WasiPlatform_UartDidTransmit: (index, data) => {
          this.emit('uartDidTransmit', index, data);
          return 0;
        },
        WasiPlatform_LinDidTransmit: (index, data) => {
          this.emit('linDidTransmit', index, data);
          return 0;
        },
        WasiPlatform_DidDetectEndlessLoop: () => {
          this.stop();
          throw new Error(`Endless loop detected`);
        },
        WasiPlatform_DidSetConfiguration: (configId, index, bufferAddr, size) => {
          let resultBuffer = Buffer.from(this.wasmMemory8.slice(bufferAddr, bufferAddr + size));
          this.emit('updatePlatformCopyConfiguration', configId, index, resultBuffer); // propagate that a new platform configuration value is available

          return 0;
        },
        WasiPlatform_ReportDebugProbe: (keyAddr, value) => {
          let index = 0, key = '';
          while (this.wasmMemory8 [keyAddr + index]) {
            key += String.fromCharCode(this.wasmMemory8 [keyAddr + index]);
            index += 1;
          }

          const keyParts = key.split('.');
          let debugProbes = this.debugProbes;
          for (index = 0; index < keyParts.length - 1; index++) {
            const id = keyParts [index];
            let debugProbe = debugProbes.find(dp => dp.id === id);
            if (!debugProbe) {
              debugProbe = {
                id,
                name: id,
                children: [],
              };

              debugProbes.push(debugProbe);
            }

            debugProbes = debugProbe.children;
          }

          const id = keyParts [keyParts.length - 1];
          let debugProbe = debugProbes.find(db => db.id === id);
          if (!debugProbe) {
            debugProbe = {
              id,
              name: '',
            };

            debugProbes.push(debugProbe);
          }

          debugProbe.name = `${id} = ${value}`;
        },
      },
    };

    this.wasmInstance = await WebAssembly.instantiate(this.wasmModule, wasmImports);
    this.isInitialized = false;
  }

  initialize(handler) {
    if (this.isInitialized) {
      throw new Error('Should never happen!');
    }
    this.isInitialized = true;
    let error = 0;
    if (handler) {
      this.initializeHandler = handler;
    } else {
      handler = this.initializeHandler;
    }

    const defaultOptions = {
      sharedBufferSize: 6 * 1024 * 1024,
      storage: null,
      storageTotalCapacity: 4 * 1024 * 1024,
    };

    const options = handler ? handler(defaultOptions) : defaultOptions;

    const { exports } = this.wasmInstance;

    if (error >= 0) {
      error = exports.WasiPlatform_Initialize(4 * 1024 * 1024, 16 * 1024 * 1024);
    }

    if (error >= 0) {
      this.sharedBufferSize = options.sharedBufferSize;
      this.sharedBufferAddr = exports.WasiPlatform_Allocate(this.sharedBufferSize, 1, 0);
    }

    // if (error >= 0) {
    //   error = exports.WasiPlatform_SetParameterizationOnly(0);
    // }

    if (error >= 0) {
      error = exports.WasiPlatform_SetValidRtcTime((Date.now() / 1000 - 978307200) | 0);
    }

    if (error >= 0) {
      error = exports.WasiPlatform_SetStorageTotalCapacity(options.storageTotalCapacity);
    }

    this.storageToImport = options.storage;

    if (options.biosCustomizationId != null) {
      error = exports.WasiPlatform_SetBiosCustomizationId(options.biosCustomizationId);
    }

    if (options.preInitializeCallback) {
      options.preInitializeCallback(this);
    }

    if (error >= 0) {
      error = exports.WasiPlatform_InitializeApplication();
    }

    if (error >= 0) {
      const addr1 = this.sharedBufferAddr + 0;
      const addr2 = this.sharedBufferAddr + 4;
      this.wasmMemory32 [addr1 >> 2] = 0;
      this.wasmMemory32 [addr2 >> 2] = 0;

      error = exports.WasiPlatform_GetDisplayBufferAndSize(addr1, addr2);

      if (error >= 0) {
        const displayBufferAddr = this.wasmMemory32 [addr1 >> 2];
        const displayBufferSize = this.wasmMemory32 [addr2 >> 2];
        this.displayBuffer8 = new Uint8Array(this.wasmMemory.buffer, displayBufferAddr, displayBufferSize);
      }
    }

    if (error < 0) {
      exports.WasiPlatform_PrintAndFreeError(error);
    }
  }

  start() {
    this.interval = state.setInterval((msElapsed) => {
      try {
        let error = 0;
        const { exports } = this.wasmInstance;

        if (msElapsed != null) {
          // TODO(daniel): possible correct RTC time here
        }
        if (error >= 0) {
          error = exports.WasiPlatform_StepApplication();
        }
        if (error < 0) {
          exports.WasiPlatform_PrintAndFreeError(error);
        }
      } catch (err) {
        if (err instanceof PlatformResetError) {
          // this.reset();
          this.emit('appDidPerformReset');
        } else {
          throw err;
        }
      }
    }, 5);
  }

  readBufferFromMemory(addr, length) {
    if ((addr + length) > this.wasmMemory8.length) {
      throw new Error(`Not enough space in WASM memory`);
    }

    return Buffer.from(this.wasmMemory8.slice(addr, addr + length));
  }

  writeBufferToMemory(buffer, addr) {
    if ((addr + buffer.length) > this.wasmMemory8.length) {
      throw new Error(`Not enough space in WASM memory`);
    }

    for (let i = 0; i < buffer.length; i++) {
      this.wasmMemory8 [addr + i] = buffer [i];
    }

    return buffer.length;
  }

  readCStringFromMemory(addr) {
    let length = 0;
    while (this.wasmMemory8 [addr + length] != 0) {
      length += 1;
    }

    const decoder = new TextDecoder();

    const string = decoder.decode(this.wasmMemory8.slice(addr, addr + length));

    return string;
  }

  writeCStringToMemory(string, addr, maxSize) {
    const encoder = new TextEncoder();

    const view = encoder.encode(string);

    if ((view.length + 4) >= maxSize) {
      throw new Error(`Not enough space to encode string`);
    }

    for (let i = 0; i < view.length; i++) {
      this.wasmMemory8 [addr + i] = view [i];
    }

    let nextAddr = addr + view.length;
    this.wasmMemory8 [nextAddr] = 0;
    nextAddr += 1;

    nextAddr = ((nextAddr + 3) >> 2) << 2;
    return (nextAddr - addr);
  }

  stop() {
    state.clearInterval(this.interval);
    this.interval = null;
  }

  isDisplayInvalidated() {
    const { exports } = this.wasmInstance;

    return exports.WasiPlatform_IsDisplayInvalidated();
  }

  getDisplayBuffer() {
    return this.displayBuffer8;
  }

  setStorageHasAutoInvalidate(enabled) {
    const { exports } = this.wasmInstance;
    return exports.WasiPlatform_SetStorageHasAutoInvalidate(enabled);
  }

  setButtonState(type, state) {
    let error = 0;

    const { exports } = this.wasmInstance;

    if (error >= 0) {
      error = exports.WasiPlatform_SetButtonState(type, state);
    }

    if (error < 0) {
      exports.WasiPlatform_PrintAndFreeError(error);
    }
  }

  getSensorConversion(nr, subIndex) {
    let error = 0;

    const { exports } = this.wasmInstance;

    let conversion = 0;
    if (error >= 0) {
      const addr1 = this.sharedBufferAddr + 0;
      this.wasmMemory32 [addr1 >> 2] = 0;

      error = exports.WasiPlatform_GetSensorConversion(nr, subIndex, addr1);

      if (error >= 0) {
        conversion = this.wasmMemory32 [addr1 >> 2];
      }
    }

    if (error < 0) {
      exports.WasiPlatform_PrintAndFreeError(error);
    }

    return conversion;
  }

  setInputValueForType(index, type, value) {
    let error = 0;

    const { exports } = this.wasmInstance;

    if (error >= 0) {
      error = exports.WasiPlatform_SetInputValue(index, type, value);
    }

    if (error < 0) {
      exports.WasiPlatform_PrintAndFreeError(error);
    }
  }

  isLedInvalidated() {
      const { exports } = this.wasmInstance;

      return exports.WasiPlatform_IsLedInvalidated();
  }

  getLedStaticState(index) {
      let error = 0;

      const { exports } = this.wasmInstance;

      let state = 0;
      if (error >= 0) {
        const addr1 = this.sharedBufferAddr + 0;
        this.wasmMemory32 [addr1 >> 2] = 0;

        error = exports.WasiPlatform_GetLedState(index, addr1);

        if (error >= 0) {
            state = this.wasmMemory32 [addr1 >> 2];
        }
      }

      if (error < 0) {
          exports.WasiPlatform_PrintAndFreeError(error);
      }

      return state;
  }

  isOutputInvalidated() {
    const { exports } = this.wasmInstance;

    return exports.WasiPlatform_IsOutputInvalidated();
  }

  setPlatformConfiguration(configId, index, content) {
    let error = 0;
    const { exports } = this.wasmInstance;

    if (error >= 0) {
      const buffer = this.sharedBufferAddr + 0;
      const bufferLength = this.writeBufferToMemory(content, buffer);
      error = exports.WasiPlatform_SetConfiguration(configId, index, buffer, bufferLength);
    }

    if (error < 0) {
      exports.WasiPlatform_PrintAndFreeError(error);
    }
  }


  copyPlatformConfiguration(configId, index) {
    let error = 0;
    const { exports } = this.wasmInstance;


    let buffer = null;

    if (error >= 0) {
      const addr1 = this.sharedBufferAddr + 0;
      const addr2 = addr1 + 4;
      this.wasmMemory32 [addr1 >> 2] = 0;
      error = exports.WasiPlatform_CopyConfiguration(configId, index, addr2, this.sharedBufferSize - 4, addr1);

      if (error >= 0) {
        const length = this.wasmMemory32 [addr1 >> 2];
        buffer = Buffer.from(this.wasmMemory8.slice(addr2, addr2 + length));
      }
    }

    if (error < 0) {
      exports.WasiPlatform_PrintAndFreeError(error);
    } else {
      this.emit('updatePlatformCopyConfiguration', configId, index, buffer); // propagate that a new platform configuration value is available
    }
  }

  getOutputValueAndType(index) {
    let error = 0;

    const { exports } = this.wasmInstance;

    let type = 0;
    let value = 0;
    if (error >= 0) {
      const addr1 = this.sharedBufferAddr + 0;
      const addr2 = this.sharedBufferAddr + 4;
      this.wasmMemory32 [addr1 >> 2] = 0;
      this.wasmMemory32 [addr2 >> 2] = 0;

      error = exports.WasiPlatform_GetOutputValue(index, addr1, addr2);

      if (error >= 0) {
        type = this.wasmMemory32 [addr1 >> 2];
        value = this.wasmMemory32 [addr2 >> 2];
      }
    }

    if (error < 0) {
      exports.WasiPlatform_PrintAndFreeError(error);
    }
    this.emit('updateOutputValue', index, type, value); // propagate that a new output value is available
    return { type, value };
  }

  sendUartData(index, buffer) {
    let error = 0;

    const { exports } = this.wasmInstance;

    let offset = 0;
    while ((error >= 0) && (offset < buffer.length)) {
      error = exports.WasiPlatform_ReceiveUartSingle(index, buffer [offset]);

      offset += 1;
    }

    if (error < 0) {
      exports.WasiPlatform_PrintAndFreeError(error);
    }
    this.emit('uartDidReceive', index, buffer);
  }

  sendLinData(index, buffer) {
    let error = 0;

    const { exports } = this.wasmInstance;

    let offset = 0;
    while ((error >= 0) && (offset < buffer.length)) {
      error = exports.WasiPlatform_ReceiveLinSingle(index, buffer [offset]);

      offset += 1;
    }

    if (error < 0) {
      exports.WasiPlatform_PrintAndFreeError(error);
    }
  }

  setMenuLanguage(language) {
    let error = 0;

    const { exports } = this.wasmInstance;

    error = exports.WasiPlatform_SetMenuLanguage(language);

    if (error < 0) {
      exports.WasiPlatform_PrintAndFreeError(error);
    }

  }

  isStorageInvalidated() {
    const { exports } = this.wasmInstance;

    return !!exports.WasiPlatform_IsStorageInvalidated();
  }

  exportStorage() {
    let error = 0;

    const { exports } = this.wasmInstance;

    const addr1 = this.sharedBufferAddr + 0;
    const addr2 = addr1 + 4;
    this.wasmMemory32 [addr1 >> 2] = 0;

    error = exports.WasiPlatform_ExportStorage(addr2, this.sharedBufferSize - 4, addr1);

    let buffer = null;
    if (error >= 0) {
      const length = this.wasmMemory32 [addr1 >> 2];
      buffer = Buffer.from(this.wasmMemory8.slice(addr2, addr2 + length));
    }

    if (error < 0) {
      exports.WasiPlatform_PrintAndFreeError(error);
    }

    return buffer;
  }

  setFileTotalCapacity(totalCapacity) {
    let error = 0;

    const { exports } = this.wasmInstance;

    if (error >= 0) {
      error = exports.WasiPlatform_SetFileTotalCapacity(totalCapacity);
    }

    if (error < 0) {
      exports.WasiPlatform_PrintAndFreeError(error);
    }
  }

  setFileState(isAvailable, isReadOnly) {
    let error = 0;

    const { exports } = this.wasmInstance;

    if (error >= 0) {
      error = exports.WasiPlatform_SetFileState(isAvailable, isReadOnly);
    }

    if (error < 0) {
      exports.WasiPlatform_PrintAndFreeError(error);
    }
  }

  readFile(filename) {
    let error = 0;

    const { exports } = this.wasmInstance;

    const filenameAddr = this.sharedBufferAddr;
    const filenameLength = this.writeCStringToMemory(filename, filenameAddr, this.sharedBufferSize);

    const lengthResultAddr = filenameAddr + filenameLength;

    const bufferAddr = lengthResultAddr + 4;
    const maxBufferSize = this.sharedBufferSize - filenameLength - 4;

    if (error >= 0) {
      error = exports.WasiPlatform_ReadFile(filenameAddr, bufferAddr, maxBufferSize, lengthResultAddr);
    }

    let buffer;
    if (error >= 0) {
      const length = this.wasmMemory32 [lengthResultAddr >> 2];
      buffer = Buffer.from(this.wasmMemory8.slice(bufferAddr, bufferAddr + length));
    }

    if (error < 0) {
      exports.WasiPlatform_PrintAndFreeError(error);
    }

    return buffer;
  }

  writeFile(filename, buffer) {
    let error = 0;

    const { exports } = this.wasmInstance;

    const filenameAddr = this.sharedBufferAddr;
    const filenameLength = this.writeCStringToMemory(filename, filenameAddr, this.sharedBufferSize);

    const bufferAddr = filenameAddr + filenameLength;
    const maxBufferSize = this.sharedBufferSize - filenameLength;
    if (buffer.length > maxBufferSize) {
      throw new Error(`Not enough space to store file`);
    }

    for (let i = 0; i < buffer.length; i++) {
      this.wasmMemory8 [bufferAddr + i] = buffer [i];
    }

    if (error >= 0) {
      error = exports.WasiPlatform_WriteFile(filenameAddr, bufferAddr, buffer.length);
    }

    if (error < 0) {
      exports.WasiPlatform_PrintAndFreeError(error);
    }
  }

  readDirTree() {
    let error = 0;

    const { exports } = this.wasmInstance;

    const addr1 = this.sharedBufferAddr + 0;
    const addr2 = addr1 + 4;
    this.wasmMemory32 [addr1 >> 2] = 0;

    error = exports.WasiPlatform_ReadDirTree(addr2, this.sharedBufferSize - 4, addr1);

    let filenames = null;
    if (error >= 0) {
      const length = this.wasmMemory32 [addr1 >> 2];
      const buffer = Buffer.from(this.wasmMemory8.slice(addr2, addr2 + length));

      filenames = cbor.decodeFirstSync(buffer);
    }

    if (error < 0) {
      exports.WasiPlatform_PrintAndFreeError(error);
    }

    return filenames;
  }

  setBiosSdCardRootDir(dirname) {
    let error = 0;

    const { exports } = this.wasmInstance;

    const dirnameAddr = this.sharedBufferAddr;
    this.writeCStringToMemory(dirname, dirnameAddr, this.sharedBufferSize);

    if (error >= 0) {
      error = exports.WasiPlatform_SetBiosSdCardRootDir(dirnameAddr);
    }

    if (error < 0) {
      exports.WasiPlatform_PrintAndFreeError(error);
    }
  }

  setBiosUpdateFilePattern(updatefilepattern) {
    let error = 0;

    const { exports } = this.wasmInstance;

    const updatefilepatternAddr = this.sharedBufferAddr;
    this.writeCStringToMemory(updatefilepattern, updatefilepatternAddr, this.sharedBufferSize);

    if (error >= 0) {
      error = exports.WasiPlatform_SetBiosUpdateFilePattern(updatefilepatternAddr);
    }

    if (error < 0) {
      exports.WasiPlatform_PrintAndFreeError(error);
    }
  }

}
